/** @jsxImportSource @emotion/react */
import { useState, useEffect } from "react";
import { formatFecha, accessAPI } from "../../shared";

import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";

const MAX_FILAS_SUPERIOR = 6;
const MAX_FILAS_INFERIOR = 10;

const iconosVacio = ["pi-sparkles", "pi-inbox", "pi-gift", "pi-face-smile", "pi-crown", "pi-check", "pi-heart", "pi-star", "pi-thumbs-up", "pi-verified", "pi-trophy"];
const iconoAleatorio = () => iconosVacio[Math.floor(Math.random() * iconosVacio.length)];

const truncarTexto = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};


function TituloSeccion({ titulo, total, maxFilas, color }) {
  const excedente = total - maxFilas;
  return (
    <div css={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "0 0 0.3rem 0", flexShrink: 0 }}>
      <h2 css={{ margin: 0, fontSize: "1rem", color }}>
        {titulo}
        <span css={{ marginLeft: "0.5rem", fontSize: "1.4rem", fontWeight: "bold" }}>
          {total}
        </span>
      </h2>
      {excedente > 0 && (
        <span
          css={{
            backgroundColor: color,
            color: "white",
            padding: "0.2rem 0.8rem",
            borderRadius: "1rem",
            fontSize: "0.6rem",
            fontWeight: "bold",
          }}
        >
          +{excedente} no mostrados
        </span>
      )}
    </div>
  );
}

function ListaConLimite({ items, renderItem, renderEncabezado, maxFilas }) {
  const visibles = items.slice(0, maxFilas);
  const fontSize = items.length > maxFilas - 2 ? "0.7rem" : "0.8rem";
  const padding = items.length > maxFilas - 2 ? "0.15rem 0.4rem" : "0.3rem 0.4rem";

  return (
    <div css={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      {renderEncabezado()}
      {visibles.map((item, i) => renderItem(item, i, fontSize, padding))}
    </div>
  );
}

export default function TableroSeguimiento() {
  const [datos, setDatos] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    accessAPI("GET", "admin/tablero", null,
      (respuesta) => { setDatos(respuesta); setLoading(false); },
      () => setLoading(false)
    );
    const intervalo = setInterval(() => window.location.reload(), 10 * 60 * 1000);
    return () => clearInterval(intervalo);
  }, []);

  if (loading)
    return (
      <div css={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <ProgressSpinner />
      </div>
    );

  return (
    <div css={{ padding: "1rem", height: "100vh", display: "flex", flexDirection: "column", gap: "1rem", overflow: "hidden" }}>
      {/* Sección superior: urgentes */}
      <div css={{ display: "flex", gap: "1rem", flex: "0 0 38%", minHeight: 0 }}>
        <Card
          css={{
            flex: 1,
            minHeight: 0,
            "&.p-card": { backgroundColor: datos?.solicitudes?.length > 0 ? "var(--rojoLight)" : "var(--verdeLight)" },
            "& .p-card-body": { height: "100%", padding: "1rem 1.5rem" },
            "& .p-card-content": { height: "100%", padding: 0 },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <TituloSeccion titulo="Solicitudes sin procesar" total={datos?.solicitudes?.length || 0} maxFilas={MAX_FILAS_SUPERIOR} color={datos?.solicitudes?.length > 0 ? "var(--rojo)" : "var(--verde)"} />
            <div css={{ flex: 1, minHeight: 0 }}>
              {datos?.solicitudes?.length > 0 ? (
                <ListaConLimite
                  items={datos.solicitudes}
                  maxFilas={MAX_FILAS_SUPERIOR}
                  renderEncabezado={() => (
                    <div css={{ display: "flex", padding: "0.3rem 0.4rem", borderBottom: "2px solid var(--rojo)", fontWeight: "bold", fontSize: "0.7rem", color: "var(--rojoAcento)" }}>
                      <span css={{ flex: 2 }}>Solicitud</span>
                      <span css={{ flexShrink: 0, textAlign: "right" }}>Fecha</span>
                    </div>
                  )}
                  renderItem={(s, i, fontSize, padding) => (
                    <div key={s.id} css={{ display: "flex", alignItems: "center", padding, borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize }}>
                      <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                        <span css={truncarTexto}>{s.nombrefantasia}</span>
                        <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{s.cliente.razonsocial}</span>
                      </span>
                      <span css={{ flexShrink: 0, textAlign: "right", overflow: "visible" }}>{formatFecha(s.fecha)}</span>
                    </div>
                  )}
                />
              ) : (
                <div css={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, height: "100%" }}>
                  <i className={`pi ${iconoAleatorio()}`} css={{ fontSize: "5rem", opacity: 0.5, color: "var(--verde)" }} />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Vencimiento de certificados */}
        <Card
          css={{
            flex: 1,
            minHeight: 0,
            "&.p-card": { backgroundColor: "white" },
            "& .p-card-body": { height: "100%", padding: "1rem 1.5rem" },
            "& .p-card-content": { height: "100%", padding: 0 },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <TituloSeccion titulo="Vencimientos" total={datos?.certificados?.length || 0} maxFilas={MAX_FILAS_SUPERIOR} color="var(--principal)" />
            <div css={{ flex: 1, minHeight: 0 }}>
              {datos?.certificados?.length > 0 ? (
                <ListaConLimite
                  items={datos.certificados}
                  maxFilas={MAX_FILAS_SUPERIOR}
                  renderEncabezado={() => (
                    <div css={{ display: "flex", padding: "0.3rem 0.4rem", borderBottom: "2px solid var(--principal)", fontWeight: "bold", fontSize: "0.7rem", color: "var(--principal)" }}>
                      <span css={{ flex: 2 }}>Certificado</span>
                      <span css={{ flexShrink: 0, textAlign: "right" }}>Vencimiento</span>
                    </div>
                  )}
                  renderItem={(c, i, fontSize, padding) => (
                    <div
                      key={c.id}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        padding,
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                        fontSize,
                        backgroundColor: c.dias < 0 ? "rgba(205,39,53,0.08)" : c.dias <= 7 ? "rgba(255,193,7,0.1)" : "transparent",
                      }}
                    >
                      <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                        <span css={truncarTexto}>CP-{c.proceso.nombre}</span>
                        <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{c.proceso.cliente?.razonsocial}</span>
                      </span>
                      <span
                        css={{
                          flexShrink: 0,
                          textAlign: "right",
                          overflow: "visible",
                          color: c.dias < 0 ? "var(--rojo)" : undefined,
                          fontWeight: c.dias < 0 ? "bold" : undefined,
                        }}
                      >
                        {formatFecha(c.fechavencimiento)}
                      </span>
                    </div>
                  )}
                />
              ) : (
                <div css={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, height: "100%" }}>
                  <i className={`pi ${iconoAleatorio()}`} css={{ fontSize: "5rem", opacity: 0.5, color: "var(--verde)" }} />
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Propuestas sin aceptar - oculto temporalmente
        <Card
          css={{
            flex: 1,
            minHeight: 0,
            "&.p-card": { backgroundColor: datos?.propuestas?.length > 0 ? "var(--rojoLight)" : "var(--verdeLight)" },
            "& .p-card-body": { height: "100%", padding: "1rem 1.5rem" },
            "& .p-card-content": { height: "100%", padding: 0 },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <TituloSeccion titulo="Propuestas sin aceptar" total={datos?.propuestas?.length || 0} maxFilas={MAX_FILAS_SUPERIOR} color={datos?.propuestas?.length > 0 ? "var(--rojo)" : "var(--verde)"} />
            <div css={{ flex: 1, minHeight: 0 }}>
              {datos?.propuestas?.length > 0 ? (
                <ListaConLimite
                  items={datos.propuestas}
                  maxFilas={MAX_FILAS_SUPERIOR}
                  renderEncabezado={() => (
                    <div css={{ display: "flex", padding: "0.3rem 0.4rem", borderBottom: "2px solid var(--rojo)", fontWeight: "bold", fontSize: "0.7rem", color: "var(--rojoAcento)" }}>
                      <span css={{ flex: 2 }}>Solicitud</span>
                      <span css={{ flexShrink: 0, textAlign: "right" }}>Enviada</span>
                    </div>
                  )}
                  renderItem={(p, i, fontSize, padding) => (
                    <div key={p.id} css={{ display: "flex", alignItems: "center", padding, borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize }}>
                      <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                        <span css={truncarTexto}>{p.solicitud.nombrefantasia}</span>
                        <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{p.cliente.razonsocial}</span>
                      </span>
                      <span css={{ flexShrink: 0, textAlign: "right", overflow: "visible" }}>{formatFecha(p.fechaemision)}</span>
                    </div>
                  )}
                />
              ) : (
                <div css={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, height: "100%" }}>
                  <i className={`pi ${iconoAleatorio()}`} css={{ fontSize: "5rem", opacity: 0.5, color: "var(--verde)" }} />
                </div>
              )}
            </div>
          </div>
        </Card>
        */}
      </div>

      {/* Sección inferior: planificaciones y actividades */}
      <div css={{ flex: 1, display: "flex", gap: "1rem", minHeight: 0 }}>
        {/* Planificaciones pendientes */}
        <Card
          css={{
            flex: 1,
            minHeight: 0,
            "&.p-card": { backgroundColor: "white" },
            "& .p-card-body": { height: "100%", padding: "1rem 1.5rem" },
            "& .p-card-content": { height: "100%", padding: 0 },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <TituloSeccion titulo="Planificaciones" total={datos?.planificaciones?.length || 0} maxFilas={MAX_FILAS_INFERIOR} color="var(--principal)" />
            <div css={{ flex: 1, minHeight: 0 }}>
              {datos?.planificaciones?.length > 0 ? (
                <ListaConLimite
                  items={datos.planificaciones}
                  maxFilas={MAX_FILAS_INFERIOR}
                  renderEncabezado={() => (
                    <div css={{ display: "flex", padding: "0.3rem 0.4rem", borderBottom: "2px solid var(--principal)", fontWeight: "bold", fontSize: "0.7rem", color: "var(--principal)" }}>
                      <span css={{ flex: 2 }}>Proceso</span>
                      <span css={{ flex: 2 }}>Actividad</span>
                      <span css={{ flexShrink: 0, textAlign: "right" }}>Fecha</span>
                      <span css={{ flexShrink: 0, width: "3rem", textAlign: "center" }}>Días</span>
                    </div>
                  )}
                  renderItem={(p, i, fontSize, padding) => (
                    <div
                      key={p.id}
                      css={{
                        display: "flex",
                        alignItems: "center",
                        padding,
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                        fontSize,
                        backgroundColor: p.dias < 0 ? "rgba(205,39,53,0.08)" : p.dias <= 7 ? "rgba(255,193,7,0.1)" : "transparent",
                      }}
                    >
                      <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                        <span css={truncarTexto}>{p.proceso.nombre}</span>
                        <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{p.proceso.cliente?.razonsocial}</span>
                      </span>
                      <span css={{ flex: 2, ...truncarTexto }}>{p.actividadtipo.nombre}</span>
                      <span css={{ flexShrink: 0, textAlign: "right", overflow: "visible" }}>{formatFecha(p.fecha)}</span>
                      <span
                        css={{
                          flexShrink: 0,
                          width: "3rem",
                          textAlign: "center",
                          fontWeight: "bold",
                          color: p.dias < 0 ? "var(--rojo)" : p.dias <= 7 ? "#e6a800" : "var(--verde)",
                        }}
                      >
                        {p.dias}
                      </span>
                    </div>
                  )}
                />
              ) : (
                <p css={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.5)" }}>Sin planificaciones</p>
              )}
            </div>
          </div>
        </Card>

        {/* Actividades demoradas */}
        <Card
          css={{
            flex: 1,
            minHeight: 0,
            "&.p-card": { backgroundColor: "white" },
            "& .p-card-body": { height: "100%", padding: "1rem 1.5rem" },
            "& .p-card-content": { height: "100%", padding: 0 },
          }}
        >
          <div css={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
            <TituloSeccion titulo="Actividades demoradas" total={datos?.actividades?.length || 0} maxFilas={MAX_FILAS_INFERIOR} color="var(--principal)" />
            <div css={{ flex: 1, minHeight: 0 }}>
              {datos?.actividades?.length > 0 ? (
                <ListaConLimite
                  items={datos.actividades}
                  maxFilas={MAX_FILAS_INFERIOR}
                  renderEncabezado={() => (
                    <div css={{ display: "flex", padding: "0.3rem 0.5rem", borderBottom: "2px solid var(--principal)", fontWeight: "bold", fontSize: "0.7rem", color: "var(--principal)" }}>
                      <span css={{ flex: 2 }}>Proceso</span>
                      <span css={{ flex: 2 }}>Paso</span>
                      <span css={{ flex: 1.5 }}>Decisor</span>
                    </div>
                  )}
                  renderItem={(a, i, fontSize, padding) => {
                    const decisores = a.actividadpaso_x_auditor.map((ax) => `${ax.auditor.nombre} ${ax.auditor.apellido}`);
                    const muchos = decisores.length > 1;
                    return (
                      <div key={a.id} css={{ display: "flex", alignItems: "center", padding, borderBottom: "1px solid rgba(0,0,0,0.1)", fontSize }}>
                        <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                          <span css={truncarTexto}>{a.actividad.proceso.nombre}</span>
                          <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{a.actividad.proceso.cliente.razonsocial}</span>
                        </span>
                        <span css={{ flex: 2, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0 }}>
                          <span css={truncarTexto}>{a.nombre}</span>
                          <span css={{ ...truncarTexto, fontSize: "0.6rem", color: "rgba(0,0,0,0.45)", lineHeight: 1.2 }}>{a.actividad.actividadtipo.nombre}</span>
                        </span>
                        <span css={{ flex: 1.5, display: "flex", flexDirection: "column", justifyContent: "center", minWidth: 0, fontSize: muchos ? "0.6rem" : undefined }}>
                          {decisores.map((d, idx) => (
                            <span key={idx} css={truncarTexto}>{d}</span>
                          ))}
                        </span>
                      </div>
                    );
                  }}
                />
              ) : (
                <p css={{ fontSize: "0.85rem", color: "rgba(0,0,0,0.5)" }}>Sin actividades demoradas</p>
              )}
            </div>
          </div>
        </Card>

      </div>
    </div>
  );
}
